import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import TaskRow from "@/components/TaskRow";
import TaskDialog from "@/components/TaskDialog";
import { ArrowLeft, Plus, FolderKanban, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [collab, setCollab] = useState(null);
  const [collabs, setCollabs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [dlg, setDlg] = useState({ open: false, direction: "to_me" });

  const load = async () => {
    const p = await api.get(`/projects/${id}`);
    setProject(p.data);
    const [t, allC, c] = await Promise.all([
      api.get("/tasks", { params: { project_id: id } }),
      api.get("/collaborators"),
      api.get(`/collaborators/${p.data.collaborator_id}`),
    ]);
    setTasks(t.data); setCollabs(allC.data); setCollab(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toMe = useMemo(() => tasks.filter((t) => t.direction === "to_me"), [tasks]);
  const toThem = useMemo(() => tasks.filter((t) => t.direction === "to_them"), [tasks]);

  const toggle = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" });
    load();
  };
  const del = async (t) => {
    if (!window.confirm("Obrisati task?")) return;
    await api.delete(`/tasks/${t.id}`); load();
  };

  const deleteProject = async () => {
    if (!window.confirm("Obrisati projekat? Taskovi ostaju kao samostalni.")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Projekat obrisan");
    window.location.href = "/projekti";
  };

  if (!project) return <div className="text-ink-muted">Učitavanje...</div>;

  return (
    <div className="space-y-8">
      <Link to="/projekti" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-forest" data-testid="back-to-projekti">
        <ArrowLeft className="w-4 h-4" /> Nazad na projekte
      </Link>

      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-forest">
            <FolderKanban className="w-5 h-5" />
            <div className="overline !text-forest">Projekat</div>
          </div>
          <h1 className="mt-1 text-3xl md:text-4xl font-heading font-semibold tracking-tight" data-testid="project-detail-name">{project.name}</h1>
          {project.description ? <p className="text-ink-muted mt-2 max-w-2xl">{project.description}</p> : null}
          {collab && (
            <div className="mt-3 text-sm">
              Saradnik: <Link to={`/saradnici/${collab.id}`} className="text-forest hover:underline">{collab.name}</Link>
            </div>
          )}
        </div>
        <button onClick={deleteProject} className="btn-ghost !text-terracotta" data-testid="delete-project-btn">
          <Trash2 className="w-4 h-4" /> Obriši projekat
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Column
          title={collab ? `${collab.name} duguje meni` : "On duguje meni"}
          tone="#2C402E"
          tasks={toMe}
          onNew={() => setDlg({ open: true, direction: "to_me" })}
          onToggle={toggle}
          onDelete={del}
          testid="proj-col-to-me"
        />
        <Column
          title={collab ? `Ja dugujem ${collab.name}` : "Ja dugujem njemu"}
          tone="#C65D47"
          tasks={toThem}
          onNew={() => setDlg({ open: true, direction: "to_them" })}
          onToggle={toggle}
          onDelete={del}
          testid="proj-col-to-them"
        />
      </div>

      <TaskDialog
        open={dlg.open}
        onOpenChange={(v) => setDlg((d) => ({ ...d, open: v }))}
        collaborators={collabs}
        projects={[project]}
        defaultCollaboratorId={project.collaborator_id}
        defaultProjectId={project.id}
        defaultDirection={dlg.direction}
        onCreated={load}
      />
    </div>
  );
}

function Column({ title, tone, tasks, onNew, onToggle, onDelete, testid }) {
  return (
    <div data-testid={testid} className="surface-card">
      <div className="p-5 border-b border-line flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: tone }} />
          <div className="font-heading font-semibold">{title}</div>
          <div className="text-xs text-ink-muted">({tasks.length})</div>
        </div>
        <button className="btn-ghost !py-1.5 !px-3 text-xs" onClick={onNew} data-testid={`${testid}-add`}>
          <Plus className="w-3.5 h-3.5" /> Dodaj
        </button>
      </div>
      <div>
        {tasks.length === 0 ? (
          <div className="p-6 text-sm text-ink-muted">Nema stavki.</div>
        ) : tasks.map((t) => <TaskRow key={t.id} task={t} onToggle={onToggle} onDelete={onDelete} />)}
      </div>
    </div>
  );
}
